import React, {useRef, useState} from "react";
import {IoIosLock} from "react-icons/io";
import {Tags} from "./projectBits";
import {Scrubber, useScrubber} from "./scrubber";
import comic1 from "../assets/projects/fridge/comic1.jpg";
import comic2 from "../assets/projects/fridge/comic2.jpg";
import comic3 from "../assets/projects/fridge/comic3.jpg";
import comic4 from "../assets/projects/fridge/comic4.jpg";
import comic5 from "../assets/projects/fridge/comic5.jpg";
import faceNeutral from "../assets/projects/fridge/face-neutral.jpg";
import faceNegative from "../assets/projects/fridge/face-negative.jpg";
import facePositive from "../assets/projects/fridge/face-positive.jpg";
import absNeutral from "../assets/projects/fridge/abs-neutral.jpg";
import absNegative from "../assets/projects/fridge/abs-negative.jpg";
import absPositive from "../assets/projects/fridge/abs-positive.jpg";

// Comic panels with their width/height ratios, so they share one height and fill the row.
const COMIC = [
    {src: comic1, ratio: 348 / 412, alt: "A participant wearing a Quest 3 gestures at the fridge"},
    {src: comic2, ratio: 340 / 412, alt: "The fridge's face: \"lets take a look\""},
    {src: comic3, ratio: 290 / 412, alt: "The user takes a bottle of wine"},
    {src: comic4, ratio: 244 / 412, alt: "The fridge smiles: \"oh, looks good!\""},
    {src: comic5, ratio: 230 / 412, alt: "The fridge frowns: \"that's not yours\""},
];

const PERSONAS = [
    {name: "Appliance", quote: "\"Door open.\" \"Milk added.\"", text: "A machine reporting its status. Short, flat, factual, and no opinion on what you eat."},
    {name: "Assistant", quote: "Invested in your habits, not in you.", text: "A plain-spoken health coach that praises good choices and pushes back bluntly on junk food and excuses."},
    {name: "Companion", quote: "Lights up when you look after yourself.", text: "A close friend whose mood depends on how you treat yourself: worried, hurt, or briefly sulky when you don't."},
];

type Embodiment = "face" | "abstract";
const MOOD_COLOURS = {neutral: "#4fd18b", negative: "#4f8bff", positive: "#ffd84f"};
const EMBODIMENTS: Record<Embodiment, {label: string; description: string; moods: {key: keyof typeof MOOD_COLOURS; src: string; caption: string}[]}> = {
    face: {
        label: "Face",
        description: "A cartoon face whose animated eyes and mouth react as it talks, picking from basic emotions like happy, sad, angry or surprised.",
        moods: [
            {key: "neutral", src: faceNeutral, caption: "Wide-eyed and waiting for you"},
            {key: "negative", src: faceNegative, caption: "Narrowed eyes and a frown"},
            {key: "positive", src: facePositive, caption: "Starry eyes and a big smile"},
        ],
    },
    abstract: {
        label: "Abstract",
        description: "A floating polygonal blob ringed by waveforms that pulse with its voice. It turns rounder when it's pleased and spikier when it isn't.",
        moods: [
            {key: "neutral", src: absNeutral, caption: "Smooth, steady, gently breathing"},
            {key: "negative", src: absNegative, caption: "A sharper, more angular shell"},
            {key: "positive", src: absPositive, caption: "Rounded and glowing"},
        ],
    },
};

export function FridgePage() {
    const [embodiment, setEmbodiment] = useState<Embodiment>("face");
    const current = EMBODIMENTS[embodiment];
    const comicRef = useRef<HTMLDivElement>(null);
    const comicScrubber = useScrubber(comicRef);
    return (
        <div className="pj-page">
            <div className="fr-comic-wrap">
                <div className="fr-comic" ref={comicRef}>
                    {COMIC.map(c => <img key={c.src} src={c.src} alt={c.alt} style={{flexGrow: c.ratio}}/>)}
                </div>
                {/* only on narrow screens, where the strip scrolls */}
                <Scrubber scrubber={comicScrubber} collapseWhenFits/>
            </div>
            <div className="pj-page-head">
                <div>
                    <h2>Talking Fridge</h2>
                    <p className="pj-muted">An AR agent with feelings about your food</p>
                </div>
                <Tags tags={["Unity", "Quest 3", "OpenAI Realtime"]}/>
            </div>
            <hr/>
            <p>
                An affective AI agent living in a real mini fridge, shown through mixed reality on a Meta Quest 3. You talk to it out loud, it watches what you take out and put back, and it reacts with its own mood: cheerful when you reach for something healthy, judgemental when you grab someone else's wine. I built it to explore what happens when emotional AI moves into the everyday objects we rely on.
            </p>

            <h3>How it works</h3>
            <hr/>
            <div className="pj-info-cards">
                <div className="pj-info-card">
                    <h4>It talks back</h4>
                    <p>One multimodal model handles listening, speaking, vision and tool calls in a single realtime session, so replies come back fast enough to feel like conversation. No wake word: the mic stays open.</p>
                </div>
                <div className="pj-info-card">
                    <h4>It sees what you take</h4>
                    <p>Hand and fridge colliders detect when you reach in, and a passthrough snapshot of that moment goes to the model. It keeps its own inventory through tools so it doesn't lose track of what's inside.</p>
                </div>
                <div className="pj-info-card">
                    <h4>It has feelings</h4>
                    <p>Every turn, the model sets its emotion as a point in Pleasure–Arousal–Dominance space. That drives the colour, the glow over the fridge, and the expression it shows.</p>
                </div>
            </div>

            <h3>Persona directives</h3>
            <hr/>
            <div className="pj-info-cards">
                {PERSONAS.map(p => (
                    <div key={p.name} className="pj-info-card">
                        <h4>{p.name}</h4>
                        <p className="fr-quote">{p.quote}</p>
                        <p>{p.text}</p>
                    </div>
                ))}
            </div>

            <h3>Two embodiments</h3>
            <hr/>
            <div className="fr-toggle" role="tablist">
                {(Object.keys(EMBODIMENTS) as Embodiment[]).map(k => (
                    <button key={k} role="tab" aria-selected={k === embodiment} className={k === embodiment ? "on" : ""}
                            onClick={() => setEmbodiment(k)}>
                        {EMBODIMENTS[k].label}
                    </button>
                ))}
            </div>
            <p>{current.description} Colour carries the mood in both embodiments: green for neutral, blue for negative, yellow for positive.</p>
            <div className="fr-moods">
                {current.moods.map(m => (
                    <figure key={m.src}>
                        <img src={m.src} alt={`${current.label} embodiment, ${m.key}`}/>
                        <figcaption>
                            <b><span className="fr-dot" style={{background: MOOD_COLOURS[m.key]}}/>{m.key[0].toUpperCase() + m.key.slice(1)}</b>
                            <span className="pj-muted">{m.caption}</span>
                        </figcaption>
                    </figure>
                ))}
            </div>

            <div className="pj-panel">
                <div>
                    <h4>Study and results coming soon...</h4>
                    <p className="pj-small pj-muted">The paper is on its way.</p>
                </div>
                <span className="pj-lock"><IoIosLock/></span>
            </div>
        </div>
    );
}
